/**
 * Centralised out-of-stock logic for the storefront.
 *
 * A product/variant can be out of stock in TWO ways:
 *   1. The `isOutOfStock` flag is set (admin toggled it, regardless of quantity).
 *   2. The available quantity is 0.
 *
 * Stock is tracked at the VARIANT level. A product that uses option groups is
 * still purchased through variants, so a product is out of stock only when every
 * one of its variants is out of stock.
 */

interface StockVariant {
  id?: string;
  isOutOfStock?: boolean;
  availableQuantity?: number;
  isDefault?: boolean;
}

/** True if a single variant is unavailable (flagged out of stock OR zero quantity). */
export function isVariantOutOfStock(variant: StockVariant | null | undefined): boolean {
  if (!variant) return true;
  return Boolean(variant.isOutOfStock) || (variant.availableQuantity ?? 0) <= 0;
}

/** True if the product has no purchasable variant (all variants out of stock). */
export function isProductOutOfStock(
  product: { variants?: StockVariant[] | null } | null | undefined
): boolean {
  const variants = product?.variants ?? [];
  if (variants.length === 0) return true;
  return variants.every(isVariantOutOfStock);
}

/**
 * The first variant a customer could actually buy: prefer the default variant,
 * fall back to the first in-stock variant. Returns undefined if none are available.
 */
export function getFirstAvailableVariant<T extends StockVariant>(
  variants: T[] | null | undefined
): T | undefined {
  const list = variants ?? [];
  return (
    list.find((v) => v.isDefault && !isVariantOutOfStock(v)) ??
    list.find((v) => !isVariantOutOfStock(v))
  );
}
